import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import type {
  AccountDetail,
  AccountSearchResult,
  EditShipmentInput,
  EquipmentRef,
  PaginatedResult,
  SetInProgressResponse,
  ShipmentCalf,
  ShipmentCaneSuggestion,
  ShipmentDetail,
  ShipmentDonor,
  ShipmentEmbryo,
  ShipmentListItem,
  ShipmentPayload,
  ShipmentQueryRequest,
  ShipmentRecipient,
  ShipmentSemen,
  SiteRef,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function queryShipments(
  searchParams: URLSearchParams,
): Promise<PaginatedResult<ShipmentListItem>> {
  const url = new URL(`${getBaseUrl()}/api/v1/shipping/query`);
  url.search = searchParams.toString();
  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as PaginatedResult<ShipmentListItem>;
}

function buildShipmentQueryParams(
  request: ShipmentQueryRequest,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set('skip', String(request.skip));
  if (request.take != null) params.set('take', String(request.take));
  if (request.sortField) {
    params.set(
      'order',
      `${request.sortField}:${(request.sortDir ?? 'ASC').toLowerCase()}`,
    );
  }
  if (request.shipFromSiteId)
    params.set('shipFromSiteId', request.shipFromSiteId);
  if (request.shippingMethod != null)
    params.set('shippingMethod', String(request.shippingMethod));
  if (request.shipmentStatus != null)
    params.set('shipmentStatus', String(request.shipmentStatus));
  if (request.search) params.set('search', request.search);
  if (request.hasEmbryos) params.set('hasEmbryos', 'true');
  if (request.hasSemen) params.set('hasSemen', 'true');
  if (request.hasCalves) params.set('hasCalves', 'true');
  if (request.hasRecipients) params.set('hasRecipients', 'true');
  if (request.hasDonors) params.set('hasDonors', 'true');
  if (request.ids?.length) {
    for (const id of request.ids) params.append('ids', id);
  }
  return params;
}

export const shipmentQueryOptions = (request: ShipmentQueryRequest) =>
  queryOptions({
    queryKey: ['shipments', request],
    queryFn: () => queryShipments(buildShipmentQueryParams(request)),
  });

export const shipmentGridQueryOptions = (searchParams: URLSearchParams) =>
  queryOptions({
    queryKey: ['shipments', 'grid', searchParams.toString()],
    queryFn: () => queryShipments(searchParams),
  });

export async function fetchShipment(
  shipmentId: string,
): Promise<ShipmentDetail> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as ShipmentDetail;
}

export const shipmentOptions = (shipmentId: string) =>
  queryOptions({
    queryKey: ['shipping', 'shipment', shipmentId],
    queryFn: () => fetchShipment(shipmentId),
    enabled: !!shipmentId,
  });

export async function fetchShipmentSemen(
  shipmentId: string,
): Promise<ShipmentSemen[]> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/semen`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as ShipmentSemen[];
}

export const shipmentSemenOptions = (shipmentId: string) =>
  queryOptions({
    queryKey: ['shipping', 'semen', shipmentId],
    queryFn: () => fetchShipmentSemen(shipmentId),
    enabled: !!shipmentId,
  });

export async function fetchShipmentEmbryos(
  shipmentId: string,
): Promise<ShipmentEmbryo[]> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/embryos`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as ShipmentEmbryo[];
}

export const shipmentEmbryosOptions = (shipmentId: string) =>
  queryOptions({
    queryKey: ['shipping', 'embryos', shipmentId],
    queryFn: () => fetchShipmentEmbryos(shipmentId),
    enabled: !!shipmentId,
  });

function normalizeCaneCodes(caneCodes: (string | null | undefined)[]) {
  return Array.from(
    new Set(
      caneCodes
        .map((caneCode) => caneCode?.trim())
        .filter((caneCode): caneCode is string => Boolean(caneCode)),
    ),
  );
}

export async function fetchShipmentCaneSuggestion(
  shipmentId: string,
): Promise<ShipmentCaneSuggestion> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/embryos/unique-cane-suggestion`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const shipmentCaneSuggestion =
    (await response.json()) as ShipmentCaneSuggestion;
  return {
    ...shipmentCaneSuggestion,
    occupiedCaneCodes: normalizeCaneCodes([
      ...(shipmentCaneSuggestion.occupiedCaneCodes ?? []),
      ...(shipmentCaneSuggestion.recaneEmbryos?.map((embryo) => embryo.cane) ??
        []),
    ]),
  };
}

export const shipmentCaneSuggestionOptions = (shipmentId: string) =>
  queryOptions({
    queryKey: ['shipping', shipmentId, 'unique-cane-suggestion'],
    queryFn: () => fetchShipmentCaneSuggestion(shipmentId),
    enabled: !!shipmentId,
  });

export async function fetchShipmentDonors(
  shipmentId: string,
): Promise<ShipmentDonor[]> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/donors`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as ShipmentDonor[];
}

export const shipmentDonorsOptions = (shipmentId: string) =>
  queryOptions({
    queryKey: ['shipping', 'donors', shipmentId],
    queryFn: () => fetchShipmentDonors(shipmentId),
    enabled: !!shipmentId,
  });

export async function fetchShipmentRecipients(
  shipmentId: string,
): Promise<ShipmentRecipient[]> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/recipients`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as ShipmentRecipient[];
}

export const shipmentRecipientsOptions = (shipmentId: string) =>
  queryOptions({
    queryKey: ['shipping', 'recipients', shipmentId],
    queryFn: () => fetchShipmentRecipients(shipmentId),
    enabled: !!shipmentId,
  });

export async function fetchShipmentCalves(
  shipmentId: string,
): Promise<ShipmentCalf[]> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/calves`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as ShipmentCalf[];
}

export const shipmentCalvesOptions = (shipmentId: string) =>
  queryOptions({
    queryKey: ['shipping', 'calves', shipmentId],
    queryFn: () => fetchShipmentCalves(shipmentId),
    enabled: !!shipmentId,
  });

export async function searchAccounts(
  query: string,
): Promise<AccountSearchResult[]> {
  const url = `${getBaseUrl()}/api/v1/shipping/accounts/search?query=${encodeURIComponent(query)}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as AccountSearchResult[];
}

export async function searchEquipment(query: string): Promise<EquipmentRef[]> {
  const url = `${getBaseUrl()}/api/v1/shipping/equipment/search?query=${encodeURIComponent(query)}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as EquipmentRef[];
}

export async function searchSites(query: string): Promise<SiteRef[]> {
  const url = `${getBaseUrl()}/api/v1/shipping/sites/search?query=${encodeURIComponent(query)}`;
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as SiteRef[];
}

export async function fetchShippingSites(): Promise<SiteRef[]> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  const url = `${API_BASE_URL}/api/v1/shipping/sites`;
  console.log(url);
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as SiteRef[];
}

export const shippingSitesOptions = () =>
  queryOptions({
    queryKey: ['shipping', 'sites'],
    queryFn: fetchShippingSites,
  });

export const searchAccountsOptions = (query: string) =>
  queryOptions({
    queryKey: ['shipping', 'accounts', 'search', query],
    queryFn: () => searchAccounts(query),
  });

export const searchEquipmentOptions = (query: string) =>
  queryOptions({
    queryKey: ['shipping', 'equipment', 'search', query],
    queryFn: () => searchEquipment(query),
  });

export const searchSitesOptions = (query: string) =>
  queryOptions({
    queryKey: ['shipping', 'sites', 'search', query],
    queryFn: () => searchSites(query),
  });

export async function fetchAccountDetails(
  accountId: string,
): Promise<AccountDetail> {
  const url = `${getBaseUrl()}/api/v1/shipping/accounts/${accountId}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as AccountDetail;
}

export const accountDetailsOptions = (accountId: string) =>
  queryOptions({
    queryKey: ['shipping', 'account-details', accountId],
    queryFn: () => fetchAccountDetails(accountId),
    enabled: !!accountId,
  });

export async function setInProgress(
  shipmentId: string,
  packedById?: string | null,
): Promise<SetInProgressResponse> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/in-progress`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify({ packedById: packedById ?? null }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }
  return (await response.json()) as SetInProgressResponse;
}

export async function runShip(shipmentId: string): Promise<void> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/ship`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }
}

export async function runShipIntra(shipmentId: string): Promise<void> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/ship-intra`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }
}

export async function runShipIntraYackley(shipmentId: string): Promise<void> {
  const url = `${getBaseUrl()}/api/v1/shipping/${shipmentId}/ship-intra-yackley`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }
}

export async function editShipments(
  inputs: EditShipmentInput[],
): Promise<ShipmentPayload[]> {
  const url = `${getBaseUrl()}/api/v1/shipping`;
  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(inputs),
  });
  if (!response.ok && response.status !== 400) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error status: ${response.status}, message: ${errorText}`,
    );
  }
  return (await response.json()) as ShipmentPayload[];
}

export const useEditShipments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: EditShipmentInput[]) => editShipments(inputs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
      queryClient.invalidateQueries({ queryKey: ['allShipments'] });
    },
  });
};

export type { EditShipmentInput } from './types';
